import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import config from "../config";
import { setAccessToken } from "../apiHelpers";
import useUserStore, { userFromApi } from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import usePartsStore from "../stores/usePartsStore";
import { useToast } from "../components/Toast/ToastContext";
import { instance } from "../instance";
import { buttonClass } from "../components/ui/Button";
import { Field } from "../components/ui/Field";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";

/**
 * Invite acceptance.
 *
 * This page used to be open self-registration behind a typed invite code that was
 * compared, in this file, against a hardcoded string. That gate protected nothing:
 * the literal shipped inside the JS bundle, and POST /login/register never checked
 * it, so the page could be skipped altogether.
 *
 * The retired code is deliberately not quoted here. Anything written in this file
 * is compiled into the public bundle and its source map, which is the same mistake
 * in a smaller font. It is named in api/src/login/login-router.js, server-side.
 *
 * There is no code field any more. The invitation IS the `?invite=` token in the
 * link, verified server-side on load and again on submit. Everything identifying
 * (the email address, the capabilities, admin) comes from the invite row; all this
 * form collects is a password and, optionally, a correction to the name.
 */

type InviteInfo = {
  email: string;
  firstname: string;
  lastname: string;
};

const MIN_PASSWORD_LENGTH = 8;

const labelClass = "block mb-2 text-sm font-medium text-on-surface-variant";

const Registration = () => {
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();

  const token = new URLSearchParams(location.search).get("invite") || "";

  // 'checking' until the server rules on the link. Rendering a form first and
  // failing on submit would waste the one thing this page has to get right.
  const [phase, setPhase] = useState<"checking" | "blocked" | "ready">("checking");
  const [blockedMessage, setBlockedMessage] = useState("");
  const [invite, setInvite] = useState<InviteInfo | null>(null);

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setUser = useUserStore((state: any) => state.setUser);
  const refreshFleet = useFleetStore((state: any) => state.refreshFleet);
  const refreshParts = usePartsStore((state: any) => state.refreshParts);

  const block = useCallback((message: string) => {
    setBlockedMessage(message);
    setPhase("blocked");
  }, []);

  useEffect(() => {
    if (!token) {
      block(
        `${instance.displayName} accounts are created by invitation. ` +
        `Ask an administrator to send you one.`,
      );
      return;
    }

    let cancelled = false;

    const verify = async () => {
      try {
        const response = await fetch(
          `${config.API_ENDPOINT}/login/invite/${encodeURIComponent(token)}`,
        );
        const data = await response.json();
        if (cancelled) return;

        if (!response.ok) {
          block(data.error || "This invitation link is not valid.");
          return;
        }

        setInvite(data);
        setFirstname(data.firstname || "");
        setLastname(data.lastname || "");
        setPhase("ready");
      } catch {
        if (!cancelled) {
          block("Could not reach the server to check your invitation. Try again in a moment.");
        }
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
    // `block` is stable (useCallback, no deps) and `instance` is a module constant.
  }, [token, block]);

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== passwordConfirm) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${config.API_ENDPOINT}/login/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Accept refresh token cookie from backend
        body: JSON.stringify({
          token,
          password,
          firstname: firstname.trim(),
          lastname: lastname.trim(),
        }),
      });

      const res = await response.json();

      if (!response.ok) {
        // A rejection naming the invite itself (used, expired, cancelled) is not
        // something retyping the form can fix, so swap the whole page over rather
        // than leaving a dead form on screen under an error banner.
        if (res.reason) {
          block(res.error);
          return;
        }
        throw new Error(res.error || "Could not create your account.");
      }

      // Store access token in memory (secure!)
      setAccessToken(res.accessToken);

      // Update user store (also persists to localStorage). Every capability the
      // invite granted has to land here, or the first render gates on flags the
      // account actually holds.
      setUser(userFromApi(res.user));

      // Fetch app data
      refreshFleet();
      refreshParts();

      toast.success(`Welcome to ${instance.displayName}!`);
      history.push("/");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="flex items-center justify-center mt-10 bg-[#191919]">
      <div
        className="w-11/12 max-w-md p-6 bg-[#2a2a2a] text-white rounded-lg shadow-lg border-2 border-black"
        style={{ fontFamily: "RobotoMono, sans-serif" }}
      >
        {children}
      </div>
    </div>
  );

  if (phase === "checking") {
    return shell(
      <p className="text-center text-sm text-on-surface-variant py-4">Checking your invitation...</p>,
    );
  }

  if (phase === "blocked") {
    return shell(
      <div className="text-center">
        <h3 className="text-lg font-bold mb-3">Invitation needed</h3>
        <p className="mb-6 text-sm text-on-surface leading-relaxed">{blockedMessage}</p>
        {/* App.js has no explicit /login route: the unauthenticated switch falls
            through to Login on '*', so root is the honest way back. */}
        <Link
          to="/"
          className={buttonClass("filled", "lg")}
        >
          Back to login
        </Link>
      </div>,
    );
  }

  return shell(
    <form onSubmit={handleRegistration}>
      <h3 className="text-center text-lg font-bold mb-1">Welcome to {instance.displayName}</h3>
      <p className="text-center text-xs text-on-surface-variant mb-6">
        Set a password to finish setting up your account.
      </p>

      <Reveal show={!!error}>
        <Alert className="mb-4">{error}</Alert>
      </Reveal>

      {/* Read-only: the account is created with the invited address, so letting
          this be edited would promise something the server will not honor. */}
      <div className="mb-4">
        <label className={labelClass}>Email</label>
        <div className="w-full px-3 py-2 bg-[#242424] border border-gray-700 rounded text-on-surface-variant text-sm break-all">
          {invite?.email}
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="firstname" className={labelClass}>First Name</label>
        <Field
          size="lg"
          type="text"
          id="firstname"
          value={firstname}
          onChange={(e) => setFirstname(e.target.value)}
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="lastname" className={labelClass}>Last Name</label>
        <Field
          size="lg"
          type="text"
          id="lastname"
          value={lastname}
          onChange={(e) => setLastname(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="password" className={labelClass}>Password</label>
        <Field
          size="lg"
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <p className="mt-1 text-xs text-on-surface-variant">
          At least {MIN_PASSWORD_LENGTH} characters.
        </p>
      </div>

      <div className="mb-6">
        <label htmlFor="passwordConfirm" className={labelClass}>Confirm Password</label>
        <Field
          size="lg"
          type="password"
          id="passwordConfirm"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          required
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className={`${buttonClass("filled", "lg")} w-full`}
      >
        {submitting ? "Creating your account..." : "Create Account"}
      </button>
    </form>,
  );
};

export default Registration;
